import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import {
  TrendingUp, Image as ImageIcon, Wallet, Heart, Bot, Languages, Crown, Zap, BarChart3, Camera,
  RefreshCw, UserMinus, QrCode, Gem, MessageSquare, Rocket, ArrowRight, ArrowLeft, Sparkles, Quote,
} from "lucide-react";
import Logo from "../components/Logo";

// ---- Content -------------------------------------------------------------
const HERO_STORIES = [
  {
    Icon: TrendingUp,
    eyebrow: "01 · The headline number",
    headline: "20–30% more sales — same kitchen, same staff.",
    body:
      "Restaurants that swap text menus for a visual, AI-guided experience consistently lift order volume by 20–30%. Same chef, same recipes — guests just see the food and decide faster.",
    metric: "+27%",
    metricLabel: "Avg. order-value lift across pilot restaurants",
    bg: "#10b98115",
    accent: "#10b981",
  },
  {
    Icon: Bot,
    eyebrow: "02 · A salesperson at every seat",
    headline: "A 24/7 AI waiter that never misses an upsell.",
    body:
      "Your menu doubles as a smart sommelier. It learns the guest's mood, recommends pairings, suggests the dessert your server forgot to mention — in their own language, on their own phone.",
    metric: "3.2x",
    metricLabel: "Dishes discovered per visit",
    bg: "#E5A93C18",
    accent: "#E5A93C",
  },
  {
    Icon: Heart,
    eyebrow: "03 · Regulars on autopilot",
    headline: "Loyalty that runs without lifting a finger.",
    body:
      "Points, cashback, birthday rewards and personalised recommendations come built-in. Your regulars get the celebrity treatment automatically — they come back, you didn't even have to remember their face.",
    metric: "41%",
    metricLabel: "Repeat-visit rate after loyalty enabled",
    bg: "#ec489918",
    accent: "#ec4899",
  },
];

const BENTO = [
  { kind: "feature", Icon: Languages, title: "Speaks every language", body: "Hindi, Tamil, Marathi, Mandarin, French, Spanish — guests ask their AI sommelier in whatever language feels natural. The kitchen still reads the order in English.", span: "lg:col-span-2", accent: "#34d399" },
  { kind: "stat", value: "12s", label: "average time to first dish click", sub: "vs ~3 min on paper menus", accent: "#E5A93C" },
  { kind: "feature", Icon: Crown, title: "Push your money-makers", body: "High-margin dishes float to the top, dressed in cinema-grade photography. The algorithm respects your P&L.", accent: "#E5A93C" },
  { kind: "feature", Icon: RefreshCw, title: "Live updates · zero reprints", body: "86 a dish, change a price, launch a special — every guest sees it instantly. Your last printed menu was your last printed menu.", accent: "#06b6d4" },
  { kind: "quote", body: "We replaced our menus on a Friday. By the next Friday we'd added a fourth section — the AI kept selling things we didn't know guests wanted.", who: "Operator · 84-seat brasserie, Bengaluru", accent: "#C084FC" },
  { kind: "stat", value: "₹0", label: "spent on menu reprints", sub: "per year, forever", accent: "#10b981" },
];

const NUMBERED = [
  { Icon: ImageIcon, title: "AI-powered visual ordering", body: "Guests order with confidence when they see high-quality images and videos for every dish — and get AI dish suggestions instantly." },
  { Icon: Wallet, title: "Higher average bill value", body: "Add-ons, combos, desserts, beverages, and premium dishes are recommended automatically to lift revenue per table." },
  { Icon: Camera, title: "Instagram-grade menu experience", body: "Modern customers love visuals. A premium digital experience drives shareable, organic social moments without a marketing team." },
  { Icon: UserMinus, title: "Reduce staff dependency", body: "Even with a leaner floor team, customers can explore, understand and order smoothly through AI assistance." },
  { Icon: QrCode, title: "QR-based smart dining", body: "No app downloads. Customers scan and interact instantly from their phones — friction-free onboarding." },
  { Icon: MessageSquare, title: "Reviews & engagement, baked in", body: "Encourage dish reviews, ratings and feedback right inside the menu — and ride the algorithm on social channels." },
  { Icon: BarChart3, title: "Customer insights that compound", body: "Know what guests love, which dishes win, spending behaviour and peak trends — and act on it weekly." },
  { Icon: Gem, title: "Premium brand positioning", body: "Position your restaurant as modern, innovative and technology-led — far ahead of competitors still using laminated paper." },
  { Icon: Zap, title: "Faster ordering = faster turnover", body: "Cut confusion and wait time. Serve more covers during peak hours without expanding the team." },
  { Icon: Rocket, title: "Future-ready stack", body: "Move beyond traditional menus and give customers the AI-powered dining experience they already expect from premium brands." },
];

const MARQUEE = [
  "AI Sommelier", "Multi-language", "Real-time menu", "Loyalty engine",
  "Visual ordering", "Smart upsells", "QR-first dining", "Customer CRM",
  "Margin push", "Live KOT", "Allergen safety", "Plating videos",
  "Social-share boost", "POS integrations", "Token analytics", "Instagram visuals",
];

// ---- Component -----------------------------------------------------------
export default function WhyMnuVerse() {
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(heroProgress, [0, 1], [0, 80]);
  const heroOpacity = useTransform(heroProgress, [0, 0.8], [1, 0.2]);

  return (
    <div className="min-h-screen relative overflow-x-hidden text-white" style={{ background: "#06070A" }}>
      <Background />

      {/* ===== Top nav ===== */}
      <header className="relative z-30 max-w-7xl mx-auto flex items-center justify-between px-6 md:px-12 py-6">
        <Link to="/" className="flex items-center gap-2" data-testid="why-brand">
          <Logo size={36} />
          <span className="font-display text-2xl tracking-tight">Mnu<span className="gold-text">Verse</span></span>
        </Link>
        <nav className="flex items-center gap-1 text-sm">
          <Link to="/" className="px-3 py-1.5 text-white/70 hover:text-white inline-flex items-center gap-1" data-testid="why-back-home"><ArrowLeft className="w-3.5 h-3.5" /> Home</Link>
          <Link to="/pricing" className="px-3 py-1.5 text-white/70 hover:text-white" data-testid="why-pricing">Pricing</Link>
          <Link to="/pricing" className="ml-2 px-4 py-1.5 rounded-full gold-btn font-semibold inline-flex items-center gap-1.5" data-testid="why-trial-cta">
            Start free trial <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </nav>
      </header>

      {/* ===== Editorial hero ===== */}
      <section ref={heroRef} className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-32 grid lg:grid-cols-12 gap-8 items-end">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="lg:col-span-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-[11px] text-white/60 font-mono uppercase tracking-[0.22em]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#E5A93C]" /> Why MnuVerse · A field guide for restaurant owners
          </div>
          <h1 className="font-display leading-[0.92] mt-7 text-[clamp(2.6rem,7.8vw,7.2rem)] tracking-tight">
            Your menu has<br />
            <span className="italic" style={{ color: "#E5A93C" }}>one job —</span><br />
            <span className="text-white/85">to sell more food.</span>
          </h1>
          <p className="mt-7 text-base md:text-lg text-white/65 max-w-xl leading-relaxed">
            Paper menus retire. MnuVerse turns your menu into a 24/7 AI salesperson who learns your kitchen, knows your regulars and never forgets the upsell. Sixteen reasons follow.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/pricing" data-testid="why-hero-trial" className="gold-btn rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2">
              Start 30-day trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/r/gilded-fork" className="rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2 border border-white/15 hover:bg-white/5 transition-colors">
              See a live menu
            </Link>
          </div>
        </motion.div>

        {/* Side stat plaque */}
        <motion.aside
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="lg:col-span-4 relative"
        >
          <div className="rounded-3xl p-7 relative overflow-hidden"
            style={{
              background: "linear-gradient(165deg, rgba(229,169,60,0.22), rgba(20,16,8,0.95) 65%)",
              border: "1px solid rgba(229,169,60,0.35)",
              boxShadow: "0 30px 60px -30px rgba(229,169,60,0.45)",
            }}
            data-testid="why-hero-plaque"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Pilot data · 12 restaurants</div>
            <div className="font-display text-7xl mt-3 gold-text leading-none">+27%</div>
            <div className="text-sm text-white/75 mt-2">Average order-value lift inside 90 days.</div>
            <div className="h-px my-5" style={{ background: "linear-gradient(to right, rgba(229,169,60,0.6), transparent)" }} />
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <div className="font-display text-2xl">12s</div>
                <div className="text-white/55">time to first<br />dish click</div>
              </div>
              <div>
                <div className="font-display text-2xl">3.2x</div>
                <div className="text-white/55">dish discovery<br />per session</div>
              </div>
            </div>
          </div>
        </motion.aside>
      </section>

      {/* ===== Marquee strip ===== */}
      <section className="relative z-10 border-y border-white/5 bg-white/[0.015] py-5 overflow-hidden" data-testid="why-marquee">
        <motion.div
          className="flex gap-8 whitespace-nowrap"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ repeat: Infinity, duration: 40, ease: "linear" }}
        >
          {[...MARQUEE, ...MARQUEE].map((label, i) => (
            <span key={i} className="text-sm md:text-base font-display tracking-tight" style={{ color: i % 2 ? "rgba(255,255,255,0.55)" : "#E5A93C" }}>
              {label}<span className="text-white/20 ml-8">✦</span>
            </span>
          ))}
        </motion.div>
      </section>

      {/* ===== Three big editorial stories ===== */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24 space-y-24" data-testid="why-stories">
        {HERO_STORIES.map((s, i) => (
          <BigStory key={s.headline} story={s} reversed={i % 2 === 1} index={i} />
        ))}
      </section>

      {/* ===== Bento mid-section ===== */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24" data-testid="why-bento">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Inside the operating system</div>
            <h2 className="font-display text-4xl md:text-5xl mt-2 tracking-tight">A salesperson, <span className="italic gold-text">in glass.</span></h2>
          </div>
          <div className="text-sm text-white/55 max-w-sm">
            A few of the moving parts. Each one earns its place by either making you money or saving you sanity.
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {BENTO.map((tile, i) => (
            <BentoTile key={i} tile={tile} index={i} />
          ))}
        </div>
      </section>

      {/* ===== Numbered editorial list ===== */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 pb-32" data-testid="why-numbered">
        <div className="text-center mb-14">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Ten more reasons</div>
          <h2 className="font-display text-4xl md:text-5xl mt-2 tracking-tight">If the top three didn't sell you, <span className="italic gold-text">these will.</span></h2>
        </div>
        <ol className="divide-y divide-white/8">
          {NUMBERED.map((n, i) => (
            <NumberedRow key={n.title} n={n} idx={i} />
          ))}
        </ol>
      </section>

      {/* ===== Bottom CTA ===== */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          className="rounded-[2rem] p-10 md:p-14 text-center relative overflow-hidden"
          style={{
            background: "linear-gradient(165deg, rgba(229,169,60,0.22), rgba(20,16,8,0.95) 65%)",
            border: "1px solid rgba(229,169,60,0.4)",
            boxShadow: "0 40px 80px -30px rgba(229,169,60,0.45)",
          }}
          data-testid="why-bottom-cta"
        >
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full" style={{ background: "radial-gradient(closest-side, rgba(229,169,60,0.35), transparent)" }} />
          <div className="relative">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">No card, no commitment</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3 tracking-tight">Pick a plan. Start selling more <span className="italic gold-text">tonight.</span></h2>
            <p className="text-base text-white/65 mt-4 max-w-xl mx-auto">
              30 days free. Your QR codes go live the moment you sign up. Cancel any time before day 31 and you owe us nothing — not even an email.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to="/pricing" data-testid="why-cta-pricing" className="gold-btn rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2">
                See plans & pricing <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login/admin" className="rounded-full px-7 py-3.5 font-semibold border border-white/25 hover:bg-white/5 transition-colors">
                I already have an account
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      <footer className="relative z-10 border-t border-white/5 py-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40 font-mono">
          <div>MnuVerse — Restaurant Operating System</div>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-white/80">Home</Link>
            <Link to="/pricing" className="hover:text-white/80">Pricing</Link>
            <Link to="/why-mnuverse" className="hover:text-white/80">Why MnuVerse</Link>
            <Link to="/login/admin" className="hover:text-white/80">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ---- Sub-components ------------------------------------------------------
function Background() {
  return (
    <div className="pointer-events-none absolute inset-0 z-0">
      <div className="absolute -top-32 -left-32 w-[720px] h-[720px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(16,185,129,0.16), transparent)" }} />
      <div className="absolute top-[30%] -right-40 w-[620px] h-[620px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(229,169,60,0.18), transparent)" }} />
      <div className="absolute bottom-[10%] -left-40 w-[640px] h-[420px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(192,132,252,0.13), transparent)" }} />
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")" }} />
    </div>
  );
}

function BigStory({ story, reversed, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { Icon } = story;
  return (
    <div ref={ref} className={`grid lg:grid-cols-12 gap-8 lg:gap-12 items-center ${reversed ? "" : ""}`} data-testid={`big-story-${index}`}>
      {/* Number/metric column */}
      <motion.div
        initial={{ opacity: 0, x: reversed ? 40 : -40 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.7 }}
        className={`lg:col-span-5 ${reversed ? "lg:order-2" : ""}`}
      >
        <div
          className="rounded-3xl p-8 md:p-10 relative overflow-hidden"
          style={{
            background: `linear-gradient(165deg, ${story.bg}, rgba(12,14,18,0.97) 65%)`,
            border: `1px solid ${story.accent}40`,
            boxShadow: `0 30px 60px -30px ${story.accent}40`,
          }}
        >
          <div className="absolute -top-16 -right-16 w-60 h-60 rounded-full" style={{ background: `radial-gradient(closest-side, ${story.accent}33, transparent)` }} />
          <span
            className="relative w-12 h-12 rounded-2xl inline-flex items-center justify-center"
            style={{ background: `${story.accent}25`, color: story.accent, border: `1px solid ${story.accent}55` }}
          >
            <Icon className="w-5 h-5" />
          </span>
          <div className="relative font-display text-7xl mt-6 tracking-tight" style={{ color: story.accent }}>
            {story.metric}
          </div>
          <div className="relative text-sm text-white/65 mt-2 max-w-[18ch] leading-snug">{story.metricLabel}</div>
        </div>
      </motion.div>

      {/* Copy column */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, delay: 0.15 }}
        className={`lg:col-span-7 ${reversed ? "lg:order-1" : ""}`}
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: story.accent }}>{story.eyebrow}</div>
        <h3 className="font-display text-4xl md:text-5xl mt-3 leading-[1.04] tracking-tight">{story.headline}</h3>
        <p className="text-base md:text-lg text-white/70 mt-5 leading-relaxed max-w-xl">{story.body}</p>
      </motion.div>
    </div>
  );
}

function BentoTile({ tile, index }) {
  const baseClass = "rounded-2xl p-5 relative overflow-hidden";
  const animationProps = {
    initial: { opacity: 0, y: 12 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-50px" },
    transition: { duration: 0.45, delay: 0.04 * (index % 6) },
  };

  if (tile.kind === "stat") {
    return (
      <motion.div
        {...animationProps}
        data-testid={`bento-stat-${index}`}
        className={`${baseClass} flex flex-col justify-end min-h-[180px]`}
        style={{
          background: `linear-gradient(165deg, ${tile.accent}1f, rgba(12,14,18,0.94) 65%)`,
          border: `1px solid ${tile.accent}40`,
        }}
      >
        <div className="font-display text-6xl tracking-tight" style={{ color: tile.accent }}>{tile.value}</div>
        <div className="text-sm text-white/80 mt-2">{tile.label}</div>
        <div className="text-xs text-white/45 mt-1">{tile.sub}</div>
      </motion.div>
    );
  }

  if (tile.kind === "quote") {
    return (
      <motion.div
        {...animationProps}
        data-testid={`bento-quote-${index}`}
        className={`${baseClass} sm:col-span-2 lg:col-span-2 min-h-[180px]`}
        style={{
          background: "linear-gradient(165deg, rgba(192,132,252,0.14), rgba(12,14,18,0.94) 65%)",
          border: "1px solid rgba(192,132,252,0.35)",
        }}
      >
        <Quote className="w-7 h-7" style={{ color: tile.accent }} />
        <div className="font-display text-xl md:text-2xl mt-3 leading-snug">{tile.body}</div>
        <div className="text-[11px] mt-4 font-mono uppercase tracking-widest" style={{ color: tile.accent }}>— {tile.who}</div>
      </motion.div>
    );
  }

  // feature tile
  const { Icon } = tile;
  return (
    <motion.div
      {...animationProps}
      data-testid={`bento-feature-${index}`}
      className={`${baseClass} ${tile.span || ""} min-h-[180px]`}
      style={{
        background: "linear-gradient(165deg, rgba(255,255,255,0.04), rgba(12,14,18,0.96) 65%)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <span
        className="w-10 h-10 rounded-xl inline-flex items-center justify-center"
        style={{ background: `${tile.accent}22`, color: tile.accent, border: `1px solid ${tile.accent}55` }}
      >
        <Icon className="w-5 h-5" />
      </span>
      <div className="font-display text-xl mt-3">{tile.title}</div>
      <div className="text-sm mt-2 text-white/65 leading-relaxed">{tile.body}</div>
    </motion.div>
  );
}

function NumberedRow({ n, idx }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reversed = idx % 2 === 1;
  const { Icon } = n;
  return (
    <li
      ref={ref}
      data-testid={`numbered-${idx}`}
      className={`grid sm:grid-cols-[80px_1fr_auto] gap-5 items-baseline py-7 group ${reversed ? "" : ""}`}
    >
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={inView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.4 }}
        className="font-display text-5xl md:text-6xl text-white/15 group-hover:text-[#E5A93C] transition-colors leading-none"
      >
        {String(idx + 4).padStart(2, "0")}
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.45, delay: 0.08 }}
      >
        <div className="font-display text-2xl md:text-3xl tracking-tight">{n.title}</div>
        <div className="text-sm md:text-base text-white/60 mt-2 max-w-2xl leading-relaxed">{n.body}</div>
      </motion.div>
      <motion.span
        initial={{ opacity: 0, scale: 0.85 }}
        animate={inView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.4, delay: 0.18 }}
        className="hidden sm:inline-flex w-10 h-10 rounded-2xl items-center justify-center self-center"
        style={{ background: "rgba(229,169,60,0.12)", border: "1px solid rgba(229,169,60,0.3)", color: "#E5A93C" }}
      >
        <Icon className="w-4 h-4" />
      </motion.span>
    </li>
  );
}
